import { Link, Outlet, useNavigate } from 'react-router-dom'
import { Button } from '../../../components/ui/Button'
import { DataTable } from '../../../components/DataTable'
import { createColumnHelper } from '@tanstack/react-table'
import { useMemo } from 'react'
import { UserUpload } from '../../../lib/types/UserUpload'
import { useGetUploads } from '../../../api/uploads/useGetUploads'
import { formatCurrency } from '../../../lib/utils/formatNumber'
import { useGetUser } from '../../../api/auth/useGetUser'
import WarningIcon from '../../../components/ui/icons/WarningIcon'
import HelpIcon from '../../../components/ui/icons/HelpIcon'
import { Pencil, Trash2 } from 'lucide-react'
import { useModal } from '../../../components/useModal'
import { useDeleteUploadById } from '../../../api/uploads/useDeleteUploadById'
import { queryClient } from '../../../main'

const columnHelper = createColumnHelper<UserUpload>()
const UploadsList = (): JSX.Element => {
  const navigate = useNavigate()
  const { data: user } = useGetUser()
  const { handleModal } = useModal()
  const { mutateAsync: deleteUpload } = useDeleteUploadById()

  const columns = useMemo(() => {
    return [
      columnHelper.accessor('name', {
        header: 'Name',
        cell: (info) => (
          <Link className="text-foreground font-bold underline" to="#">
            {info.getValue()}
          </Link>
        ),
      }),
      columnHelper.accessor('description', {
        header: 'Description',
        cell: (info) => info.getValue(),
      }),
      columnHelper.accessor('upload_type', {
        header: 'Upload Type',
        cell: (info) => info.getValue(),
      }),
      columnHelper.accessor('gain_strategy', {
        header: () => (
          <div className="flex flex-row gap-2 items-center">
            <span>Gain Calculation</span>
            <HelpIcon tooltip="Chosen method for calculating gains for stock transactions" />
          </div>
        ),
        cell: (info) => info.getValue(),
      }),
      columnHelper.accessor('amount_local', {
        header: 'Total Amount',
        cell: (info) => formatCurrency(info.getValue(), info.row.original.currency_code),
      }),
      columnHelper.accessor('amount_target', {
        header: `Total Amount (${user?.target_currency_code.toUpperCase()})`,
        cell: (info) => formatCurrency(info.getValue(), user?.target_currency_code),
      }),
      columnHelper.accessor('transaction_count', {
        header: `Total Items`,
        cell: (info) => info.getValue(),
      }),
      columnHelper.display({
        id: 'warnings',
        header: '',
        cell: ({ row }) => {
          if (row.original.missing_rate_days) {
            return (
              <WarningIcon
                tooltip={`Missing exchange rate for ${row.original.missing_rate_days} items`}
              />
            )
          }
        },
      }),
      columnHelper.display({
        id: 'actions',
        header: '',
        cell: ({ row }) => {
          return (
            <div className="flex flex-row items-center gap-2">
              <Pencil
                className="cursor-pointer"
                onClick={(e) => navigate(`edit/${row.original.upload_id}`)}
                size={20}
              />
              <Trash2
                className="cursor-pointer hover:fill-white"
                onClick={async (e) => {
                  if (window.confirm('Are you sure you want to delete this upload?')) {
                    await deleteUpload({ id: row.original.upload_id })
                    await queryClient.invalidateQueries([
                      'uploads',
                      'list',
                      { pageIndex: 1, pageSize: 10 },
                    ])
                  }
                }}
              />
            </div>
          )
        },
      }),
    ]
  }, [deleteUpload, navigate, user?.target_currency_code])

  return (
    <div className={'h-screen dark:bg-slate-800'}>
      <div className="px-4 sm:px-10 py-8 flex flex-col gap-4">
        <div className="flex flex-col gap-3">
          <div className="flex flex-row justify-between items-center">
            <h2 className="text-2xl font-bold mb-4">My Uploads</h2>
            <Button
              variant={'default'}
              onClick={(e) => {
                e.preventDefault()
                navigate('create')
              }}>
              Upload
            </Button>
          </div>
          <DataTable columns={columns} fetchData={useGetUploads} initialPageSize={10} />
        </div>
      </div>
      <Outlet />
    </div>
  )
}

export default UploadsList
